import { useMemo } from 'react';
import type {
  NotificationAggregateItem,
  NotificationEntityType,
  NotificationListResponse,
} from '@magimp/shared';
import { useNotificationsList } from './useNotificationsList.js';

/**
 * @aiPurpose per-entity unread roll-up over the bell's notification list
 *   used by both row badges (total unread on a parent) and drawer tab
 *   badges (unread per routing tab). The selector is pure — it depends
 *   only on the existing `NOTIFICATIONS_LIST_QK` cache, so a single
 *   round-trip serves the bell + every row + the open drawer.
 * @aiInvariant rows with `unreadCount === 0` are dropped so a viewed
 *   aggregate cannot raise a stale dot on its parent row.
 */
export interface EntityUnreadSummary {
  entityType: NotificationEntityType;
  entityId: string;
  // Total unread aggregates targeting this entity (not the sum of
  // unreadCount — that would double-count when several events of the
  // same kind land before the user views any).
  totalUnreadAggregates: number;
  // Unread aggregate counts split by routing tab; `null` collapses
  // every routingTab=null aggregate (responsible-status) under
  // `responsibleStatus`. Tab badges read from the named slots.
  tabs: {
    chat: number;
    carriers: number;
    info: number;
    items: number;
    boxes: number;
    audit: number;
    // responsibleStatus aggregates have routingTab=null and contribute
    // only to the row badge.
    responsibleStatus: number;
  };
  // Raw unread aggregates targeting this entity, surfaced so an
  // auto-mark-viewed effect can iterate without re-scanning the list.
  unreadAggregates: NotificationAggregateItem[];
}

function makeEmptyTabs(): EntityUnreadSummary['tabs'] {
  return { chat: 0, carriers: 0, info: 0, items: 0, boxes: 0, audit: 0, responsibleStatus: 0 };
}

export function buildEntitySummaries(
  rows: NotificationListResponse,
): Map<string, EntityUnreadSummary> {
  const map = new Map<string, EntityUnreadSummary>();
  for (const row of rows) {
    if (row.unreadCount === 0) continue;
    const key = `${row.entityType}:${row.entityId}`;
    const existing =
      map.get(key) ??
      ({
        entityType: row.entityType,
        entityId: row.entityId,
        totalUnreadAggregates: 0,
        tabs: makeEmptyTabs(),
        unreadAggregates: [],
      } satisfies EntityUnreadSummary);
    existing.totalUnreadAggregates += 1;
    existing.unreadAggregates.push(row);
    if (row.routingTab !== null) {
      existing.tabs[row.routingTab] += 1;
    } else if (row.notificationType === 'responsibleStatus') {
      existing.tabs.responsibleStatus += 1;
    }
    map.set(key, existing);
  }
  return map;
}

/**
 * @aiPurpose React hook variant — subscribes to the shared list query
 *   and memoises the per-entity roll-up so consumers re-render only
 *   when an aggregate row actually changes.
 */
export function useNotificationsByEntity(): {
  byEntity: Map<string, EntityUnreadSummary>;
  isLoading: boolean;
} {
  const query = useNotificationsList();
  const rows = query.data;
  const byEntity = useMemo(() => buildEntitySummaries(rows ?? []), [rows]);
  return { byEntity, isLoading: query.isLoading };
}

export function entityKey(entityType: NotificationEntityType, entityId: string): string {
  return `${entityType}:${entityId}`;
}
